import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Layout, Menu, Table, Button, message, Tag, Statistic, Row, Col, Card
} from 'antd';
import { 
  DashboardOutlined, 
  KeyOutlined, 
  FileTextOutlined, 
  LogoutOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import { adminAPI } from '../../utils/api';
import './AdminDashboard.css';

const { Header, Sider, Content } = Layout;

const TestResults = () => {
  const navigate = useNavigate();
  const [authCodes, setAuthCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statistics, setStatistics] = useState({
    total: 0,
    used: 0,
    unused: 0
  });

  useEffect(() => {
    loadAuthCodes();
  }, []);

  const loadAuthCodes = async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getAuthCodes();
      if (response && response.success) {
        setAuthCodes(response.authCodes);
        
        // 计算统计数据
        const total = response.authCodes.length;
        const used = response.authCodes.filter(code => code.used).length;
        const unused = total - used;
        
        setStatistics({ total, used, unused });
      }
    } catch (error) {
      message.error('加载授权码数据失败');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    message.success('已退出登录');
    navigate('/XiaoXin/login');
  };

  const menuItems = [
    {
      key: 'dashboard',
      icon: <DashboardOutlined />,
      label: '仪表板',
      onClick: () => navigate('/XiaoXin/dashboard')
    },
    {
      key: 'authcodes',
      icon: <KeyOutlined />,
      label: '授权码管理',
      onClick: () => navigate('/XiaoXin/authcodes')
    },
    {
      key: 'results',
      icon: <FileTextOutlined />,
      label: '使用记录',
      onClick: () => navigate('/XiaoXin/results')
    }
  ];

  const columns = [
    {
      title: '授权码',
      dataIndex: 'code',
      key: 'code',
      render: (code) => <code style={{ fontSize: 13, fontWeight: 'bold' }}>{code}</code>
    },
    {
      title: '备注',
      dataIndex: 'note',
      key: 'note',
      render: (note) => note || '-'
    },
    {
      title: '状态',
      dataIndex: 'used',
      key: 'used',
      render: (used) => (
        <Tag color={used ? 'success' : 'default'} icon={used ? <CheckCircleOutlined /> : null}>
          {used ? '已使用' : '未使用'}
        </Tag>
      ),
      filters: [
        { text: '已使用', value: 1 },
        { text: '未使用', value: 0 }
      ],
      onFilter: (value, record) => record.used === value
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (time) => new Date(time).toLocaleString('zh-CN'),
      sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at)
    },
    {
      title: '使用时间',
      dataIndex: 'used_at',
      key: 'used_at',
      render: (time) => time ? (
        <span style={{ color: '#52c41a' }}>
          <ClockCircleOutlined style={{ marginRight: 4 }} />
          {new Date(time).toLocaleString('zh-CN')}
        </span>
      ) : (
        <span style={{ color: '#999' }}>-</span>
      ),
      sorter: (a, b) => {
        if (!a.used_at) return 1;
        if (!b.used_at) return -1;
        return new Date(a.used_at) - new Date(b.used_at);
      }
    }
  ];

  return (
    <Layout className="admin-layout">
      <Sider className="admin-sider" width={240}>
        <div className="admin-sider-header">
          <div className="admin-logo">💕 RPI</div>
          <div className="admin-subtitle">管理后台</div>
        </div>
        <Menu
          mode="inline"
          selectedKeys={['results']}
          items={menuItems}
          className="admin-menu"
        />
        <div className="admin-sider-footer">
          <Button 
            type="text" 
            icon={<LogoutOutlined />} 
            onClick={handleLogout}
            block
          >
            退出登录
          </Button>
        </div>
      </Sider>

      <Layout>
        <Header className="admin-header">
          <h1>授权码使用记录</h1>
        </Header>

        <Content className="admin-content">
          {/* 统计卡片 */}
          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col span={8}>
              <Card>
                <Statistic
                  title="总授权码数"
                  value={statistics.total}
                  prefix={<KeyOutlined />}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card>
                <Statistic
                  title="已使用"
                  value={statistics.used}
                  prefix={<CheckCircleOutlined />}
                  valueStyle={{ color: '#52c41a' }}
                  suffix={`/ ${statistics.total}`}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card>
                <Statistic
                  title="未使用"
                  value={statistics.unused}
                  prefix={<ClockCircleOutlined />}
                  valueStyle={{ color: '#faad14' }}
                  suffix={`/ ${statistics.total}`}
                />
              </Card>
            </Col>
          </Row>

          {/* 提示信息 */}
          <Card style={{ marginBottom: 16, background: '#f0f9ff', borderColor: '#91d5ff' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: 20 }}>ℹ️</span>
              <div>
                <strong>隐私保护说明：</strong>
                <span style={{ marginLeft: 8, color: '#666' }}>
                  测试报告数据仅存储在用户本地浏览器中，服务器仅记录授权码的使用时间，不存储任何测试内容和个人信息。
                </span>
              </div>
            </div>
          </Card>

          {/* 授权码使用记录表格 */}
          <Table
            columns={columns}
            dataSource={authCodes}
            loading={loading}
            rowKey="id"
            pagination={{ 
              pageSize: 20,
              showSizeChanger: true,
              showTotal: (total) => `共 ${total} 条记录`
            }}
          />
        </Content>
      </Layout>
    </Layout>
  );
};

export default TestResults;
